import { GetWeekDay } from "../../../utils/dates";

export const formatSlotLabel = (slot) => {
  if (!slot) {
    return "-";
  }
  const day = GetWeekDay(slot.weekday);
  if (slot.start_hour && slot.end_hour) {
    return `${slot.name} (${day} ${slot.start_hour}-${slot.end_hour})`;
  }
  return slot.name || "-";
};

export const studentDisplayName = (student) => {
  if (!student) {
    return "-";
  }
  if (student.member?.name) {
    return student.member.name;
  }
  const lastname = student.lastname || student.member?.lastname || "";
  const firstname = student.firstname || student.member?.firstname || "";
  return `${lastname} ${firstname}`.trim() || "-";
};

export const urgencyTag = (urgency) => {
  switch (urgency) {
    case "URGENT":
      return {
        value: "Urgent",
        severity: "danger",
        icon: "pi pi-exclamation-triangle",
        style: {
          backgroundColor: "#FEE2E2",
          color: "#991B1B",
          border: "1px solid #FCA5A5",
          fontWeight: 700,
        },
      };
    case "HIGH":
      return {
        value: "Prioritaire",
        severity: "warning",
        icon: "pi pi-flag-fill",
        style: {
          backgroundColor: "#FEF3C7",
          color: "#92400E",
          border: "1px solid #FBBF24",
          fontWeight: 700,
        },
      };
    default:
      return {
        value: "Neutre",
        severity: "info",
        icon: "pi pi-clock",
        style: {
          backgroundColor: "#E2E8F0",
          color: "#334155",
          border: "1px solid #CBD5E1",
          fontWeight: 600,
        },
      };
  }
};

export const statusTag = (status) => {
  switch (status) {
    case "APPROVED":
      return { value: "Approuvée", severity: "success" };
    case "REJECTED":
      return { value: "Refusée", severity: "danger" };
    case "CANCELLED":
      return { value: "Annulée", severity: "secondary" };
    case "DRIFTED":
      return { value: "Dérivée", severity: "warning" };
    default:
      return { value: "En attente", severity: "info" };
  }
};

export const matchTypeTag = (matchType) => {
  switch (matchType) {
    case "SWAP":
      return { value: "Échange", severity: "info" };
    case "ROTATION":
      return { value: "Rotation", severity: "warning" };
    default:
      return { value: "Directe", severity: "success" };
  }
};

export const formatRequestDate = (value) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const toRequestDateTime = (date) => {
  if (!date) {
    return "";
  }
  return date.toISOString();
};

export const timeFromDate = (date) => {
  if (!date || Number.isNaN(date.getTime())) {
    return "00:00";
  }
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};

export const combineDateAndTime = (date, time) => {
  if (!date) {
    return "";
  }
  const next = new Date(date);
  if (Number.isNaN(next.getTime())) {
    return "";
  }
  const [hours, minutes] = (time || "00:00").split(":");
  next.setHours(Number(hours) || 0, Number(minutes) || 0, 0, 0);
  return next.toISOString();
};

export const studentLevelId = (student) =>
  student?.level_id || student?.classe?.level_id || student?.level?.id || "";

export const studentClassId = (student) =>
  student?.class_id || student?.classe?.id || "";

export const requestLevelId = (item) =>
  item?.from_class?.level_id ||
  item?.from_class?.level?.id ||
  studentLevelId(item?.student) ||
  "";

export const requestClassId = (item) =>
  item?.from_class_id ||
  item?.from_class?.id ||
  studentClassId(item?.student) ||
  "";

export const slotsForClass = ({ slots, scheduleItems, classId }) => {
  if (!classId) {
    return [];
  }
  const slotById = new Map((slots || []).map((slot) => [slot.id, slot]));
  const result = [];
  const seen = new Set();
  for (const item of scheduleItems || []) {
    const itemClassId = item?.class_id || item?.classe?.id;
    const slotId = item?.slot_id || item?.slot?.id;
    if (itemClassId !== classId || !slotId || seen.has(slotId)) {
      continue;
    }
    seen.add(slotId);
    const slot = slotById.get(slotId) || item.slot;
    if (slot) {
      result.push(slot);
    }
  }
  return result;
};

export const isDeletedRecord = (item) => {
  const deletedAt = item?.deleted_at;
  if (!deletedAt) {
    return false;
  }
  if (typeof deletedAt === "object") {
    if (deletedAt.Valid === false) {
      return false;
    }
    const time = deletedAt.Time || deletedAt.time;
    if (!time || time === "0001-01-01T00:00:00Z") {
      return false;
    }
    return true;
  }
  if (typeof deletedAt === "string") {
    return deletedAt !== "" && deletedAt !== "0001-01-01T00:00:00Z";
  }
  return true;
};

export const isUsableClass = (classe) => {
  if (!classe?.id || isDeletedRecord(classe)) {
    return false;
  }
  if (classe.status === "BLOCKED" || classe.status === "DRAFT") {
    return false;
  }
  return true;
};

export const classLevelId = (classe) =>
  classe?.level_id || classe?.level?.id || "";

export const classTagStyle = (classeOrId) => {
  const resolved =
    typeof classeOrId === "object" && classeOrId
      ? classeOrId
      : { id: classeOrId, level: null };
  const levelColor =
    resolved?.level?.color ||
    resolved?.level_color ||
    resolved?.color ||
    "";

  if (levelColor && levelColor.trim() !== "") {
    return {
      backgroundColor: levelColor,
      color: "#000000",
    };
  }

  const palette = [
    { backgroundColor: "#DBEAFE", color: "#1D4ED8" },
    { backgroundColor: "#D1FAE5", color: "#047857" },
    { backgroundColor: "#FEF3C7", color: "#B45309" },
    { backgroundColor: "#EDE9FE", color: "#6D28D7" },
    { backgroundColor: "#FCE7F3", color: "#BE185D" },
    { backgroundColor: "#FFEDD5", color: "#C2410C" },
  ];
  const key = resolved?.id || "";
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash + key.charCodeAt(i) * (i + 1)) % palette.length;
  }
  return palette[hash];
};

export const buildClassesBySlotId = ({ classes, scheduleItems }) => {
  const classById = new Map(
    (classes || []).filter(isUsableClass).map((classe) => [classe.id, classe])
  );
  const classesBySlot = new Map();
  for (const item of scheduleItems || []) {
    const classId = item?.class_id || item?.classe?.id;
    const slotId = item?.slot_id || item?.slot?.id;
    const classe = classById.get(classId);
    if (!classId || !slotId || !classe || isDeletedRecord(item?.classe)) {
      continue;
    }
    if (!classesBySlot.has(slotId)) {
      classesBySlot.set(slotId, []);
    }
    const list = classesBySlot.get(slotId);
    if (!list.some((entry) => entry.id === classId)) {
      list.push(classe);
    }
  }
  for (const list of classesBySlot.values()) {
    list.sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr"));
  }
  return classesBySlot;
};

export const classesForPreferredSlot = (
  classesBySlot,
  slotId,
  levelId,
  excludeClassId
) =>
  (classesBySlot.get(slotId) || []).filter((classe) => {
    if (!isUsableClass(classe)) {
      return false;
    }
    if (excludeClassId && classe.id === excludeClassId) {
      return false;
    }
    if (levelId && classLevelId(classe) && classLevelId(classe) !== levelId) {
      return false;
    }
    return true;
  });

export const targetClassesForPreference = (pref, fallbackClasses = []) => {
  const fromPreference = Array.isArray(pref?.target_classes) ? pref.target_classes : [];
  const source = fromPreference.length ? fromPreference : fallbackClasses;
  const seen = new Set();
  return source.filter((classe) => {
    if (!isUsableClass(classe) || seen.has(classe.id)) {
      return false;
    }
    seen.add(classe.id);
    return true;
  });
};

export const requestPreferences = (item) => {
  const seen = new Set();
  const prefs = [...(item?.preferences || [])]
    .filter((pref) => {
      const key = pref?.id || `${pref?.slot_id || pref?.slot?.id || ""}:${pref?.rank || 0}`;
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    })
    .sort((a, b) => (a.rank || 0) - (b.rank || 0));
  if (prefs.length) {
    return prefs;
  }
  if (item?.requested_slot || item?.requested_slot_id) {
    return [
      {
        slot_id: item.requested_slot_id,
        slot: item.requested_slot,
        rank: 1,
      },
    ];
  }
  return [];
};

export const formatPreferredSlots = (item) => {
  const prefs = requestPreferences(item);
  if (!prefs.length) {
    return "-";
  }
  return prefs.map((pref, index) => `${index + 1}. ${formatSlotLabel(pref.slot)}`).join(" → ");
};

export const buildPreferredSlotOptions = ({
  classes,
  slots,
  scheduleItems,
  currentClassId,
  levelId,
  extraSlots = [],
}) => {
  const classById = new Map(
    (classes || [])
      .filter(isUsableClass)
      .filter((classe) => {
        if (!levelId || !classLevelId(classe)) {
          return true;
        }
        return classLevelId(classe) === levelId;
      })
      .map((classe) => [classe.id, classe])
  );
  const slotById = new Map((slots || []).map((slot) => [slot.id, slot]));
  const otherClassesBySlot = new Map();
  for (const item of scheduleItems || []) {
    const classId = item?.class_id || item?.classe?.id;
    const slotId = item?.slot_id || item?.slot?.id;
    const classe = classById.get(classId);
    if (!classId || !slotId || !classe || isDeletedRecord(item?.classe)) {
      continue;
    }
    if (!otherClassesBySlot.has(slotId)) {
      otherClassesBySlot.set(slotId, []);
    }
    const list = otherClassesBySlot.get(slotId);
    if (classId !== currentClassId && !list.some((entry) => entry.id === classId)) {
      list.push(classe);
    }
  }
  const options = [];
  const seen = new Set();
  for (const [slotId, slotClasses] of otherClassesBySlot) {
    if (!slotClasses.length) {
      continue;
    }
    const slot = slotById.get(slotId);
    if (!slot) {
      continue;
    }
    slotClasses.sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr"));
    const classNames = slotClasses.map((classe) => classe.name).filter(Boolean);
    options.push({
      id: slotId,
      label: classNames.length
        ? `${formatSlotLabel(slot)} — ${classNames.join(", ")}`
        : formatSlotLabel(slot),
      slot,
      classes: slotClasses,
    });
    seen.add(slotId);
  }
  for (const extra of extraSlots) {
    const slot = extra?.id ? extra : extra?.slot;
    const slotId = extra?.id || extra?.slot_id || slot?.id;
    if (!slotId || seen.has(slotId)) {
      continue;
    }
    const resolved = slotById.get(slotId) || slot;
    if (!resolved) {
      continue;
    }
    options.push({
      id: slotId,
      label: formatSlotLabel(resolved),
      slot: resolved,
      classes: extra?.classes || [],
    });
    seen.add(slotId);
  }
  options.sort((a, b) => a.label.localeCompare(b.label, "fr"));
  return options;
};
